import type { Transaction, TransactionPeriodVerification } from "@/types";

function normalizeTransactionName(name: string) {
  return name.trim().toLocaleLowerCase();
}

function periodIndex(period: string) {
  const [month, year] = period.split(" ").map(Number);
  return year * 12 + month - 1;
}

function periodFromIndex(index: number) {
  const year = Math.floor(index / 12);
  const month = (index % 12) + 1;
  return `${String(month).padStart(2, "0")} ${year}`;
}

function datePeriodIndex(value: string) {
  const date = new Date(value);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
}

function getUnverifiedPeriodsBefore(
  transaction: Transaction,
  cutoffIndex: number,
): TransactionPeriodVerification[] {
  const transactionId = Number(transaction.id ?? transaction.originalId);
  if (!transactionId || !transaction.dateFrom) return [];

  const startIndex = datePeriodIndex(transaction.dateFrom);
  const endIndex = transaction.dateTo
    ? datePeriodIndex(transaction.dateTo)
    : cutoffIndex - 1;
  const lastIndex = Math.min(endIndex, cutoffIndex - 1);
  const verifiedPeriods = new Set([
    ...(transaction.verification?.listOfVerifiedTransactions ?? []),
    ...(transaction.verifiedPeriods ?? []),
  ]);
  const candidateIndexes: number[] = [];

  if (transaction.recurrence === "MONTHLY") {
    for (let index = startIndex; index <= lastIndex; index += 1) {
      candidateIndexes.push(index);
    }
  } else if (transaction.recurrence === "YEARLY") {
    const recurrenceMonth = startIndex % 12;
    const firstYear = Math.floor(startIndex / 12);
    const lastYear = Math.floor(lastIndex / 12);

    for (let year = firstYear; year <= lastYear; year += 1) {
      const index = year * 12 + recurrenceMonth;
      if (index >= startIndex && index <= lastIndex) {
        candidateIndexes.push(index);
      }
    }
  } else if (transaction.recurrence === "ONCE" && startIndex <= lastIndex) {
    candidateIndexes.push(startIndex);
  }

  return candidateIndexes.reduce<TransactionPeriodVerification[]>(
    (verifications, index) => {
      const period = periodFromIndex(index);
      if (!verifiedPeriods.has(period)) {
        verifications.push({ transactionId, period });
      }
      return verifications;
    },
    [],
  );
}

export function hasUnverifiedTransactionPeriods(
  transactions: Transaction[],
  currentPeriod: string,
) {
  const nextPeriodIndex = periodIndex(currentPeriod) + 1;
  return transactions.some(
    (transaction) =>
      transaction.transactionType === "INCOME" &&
      getUnverifiedPeriodsBefore(transaction, nextPeriodIndex).length > 0,
  );
}

export function getPastUnverifiedTransactionPeriods(
  transactions: Transaction[],
  selectedTransaction: Transaction,
  currentPeriod: string,
): TransactionPeriodVerification[] {
  const currentIndex = periodIndex(currentPeriod);
  const selectedName = normalizeTransactionName(selectedTransaction.name);
  const uniqueVerifications = new Map<string, TransactionPeriodVerification>();

  transactions.forEach((transaction) => {
    if (
      normalizeTransactionName(transaction.name) !== selectedName ||
      transaction.transactionType !== selectedTransaction.transactionType
    ) {
      return;
    }

    getUnverifiedPeriodsBefore(transaction, currentIndex).forEach(
      (verification) => {
        uniqueVerifications.set(
          `${verification.transactionId}-${verification.period}`,
          verification,
        );
      },
    );
  });

  return Array.from(uniqueVerifications.values()).sort(
    (a, b) => periodIndex(a.period) - periodIndex(b.period),
  );
}
